'use client';

import { memo } from 'react';

/**
 * The scene's text layer (TT-1150): every wrapped line as a plain SVG
 * <text> at its baseline start, tilted like its line, revealed by the
 * board-style line intro (styles/tapestryAnimations.css .scene-line-in) at
 * the delay the reveal schedule gave its entry. No pen, no dust, no
 * per-word gating: a line is on the page the moment its box is.
 *
 * Renders inside the composition's viewBox SVG, so font sizes are scene
 * units and scale with the wall. The rotation is an attribute on the outer
 * group so the intro's CSS transform on the text never fights it; the group
 * carries the entry's id (`data-sid`) so the canvas can glide a moved line.
 * Memoized: `texts` is a new array per solve and `delays` one Map per scene
 * (mutated in place), while the host re-renders every poll.
 *
 * With `onSentClick`, every VERBATIM line — a quote, the sentence as it
 * was spoken — is a link to that sentence (`sent`, the planner's index):
 * the host shows that moment of the transcript for context (TT-1168 note
 * 5), under a magnifier cursor, since the click looks something up rather
 * than acting. A paraphrase (a claim, a label, a fragment) has no one
 * sentence to show and stays inert, as the art and shapes are (TT-1174
 * note 5). Without the handler the whole layer is inert. A link's click is
 * its own: it stops there, so a host that takes clicks on the drawing (a
 * note on a spot) does not take this one too — the board's listener does
 * the same.
 */
const INERT = { pointerEvents: 'none' };
const LINK_STYLE = { pointerEvents: 'auto', cursor: 'zoom-in' };

function SceneText({ texts, delays, onSentClick = null }) {
  const live = typeof onSentClick === 'function';
  return (
    <g style={INERT}>
      {texts.map((t) =>
        t.starts.map((st, i) => {
          const line = t.uppercase ? t.lines[i].toUpperCase() : t.lines[i];
          const deg = st.tilt ? (Math.atan(st.tilt) * 180) / Math.PI : 0;
          const delay = delays.get(t.id);
          const link = live && t.verbatim && t.sent != null;
          return (
            <g
              key={`${t.id}:${i}`}
              data-sid={t.id}
              transform={deg ? `rotate(${deg.toFixed(2)} ${st.x} ${st.y})` : undefined}
              style={link ? LINK_STYLE : undefined}
              onClick={
                link
                  ? (e) => {
                      e.stopPropagation();
                      onSentClick(t.sent, t.id);
                    }
                  : undefined
              }
            >
              <text
                className="scene-line-in"
                x={st.x}
                y={st.y}
                fontSize={t.fontSize}
                fontFamily={`'${t.fontFamily}', sans-serif`}
                fill={t.color}
                style={{ whiteSpace: 'pre', animationDelay: delay ? `${delay}ms` : undefined }}
              >
                {line}
              </text>
            </g>
          );
        })
      )}
    </g>
  );
}

export default memo(SceneText);
